import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import type { BannerSwap } from "../../../utils/@types";
import { apiService } from "../../../api/api";
import { bannerDashboardApi } from "../../../api/bannerDashboardApi";
import BannerSwapForm from "../../BannerSwap/BannerSwapComponents/BannerSwapForm/BannerSwapForm";
import SwapQueueFilterBar from "../components/SwapQueueFilterBar";
import SwapQueueDetailsDialog from "../components/SwapQueueDetailsDialog";
import SwapScheduleDateCell from "../components/SwapScheduleDateCell";
import {
  channelResultsSummary,
} from "../bannerDashboardUtils";
import { getBannerSwapStatusStyle } from "../bannerSwapStatusStyles";
import {
  parseSwapQueuePrefill,
  type SwapQueuePrefill,
} from "../../BannerSwap/BannerSwapComponents/bannerSwapQueueNavigation";
import { useSwapQueueCancellation } from "../useSwapQueueCancellation";
import { BannerDashboardTabLayout } from "../bannerDashboardLayout";
import "../bannerSwapDialogs.css";
import { trackToolAction } from "../../../utils/analytics";
import { parseBooleanParam, parseStringArrayParam } from "../../../utils/urlFilters";

function statusBadge(rowData: BannerSwap): React.ReactNode {
  const style = getBannerSwapStatusStyle(rowData.status);

  return (
    <span
      className="text-center px-3 py-1 rounded-full text-sm font-medium"
      style={{
        backgroundColor: style.backgroundColor,
        color: style.textColor,
      }}
    >
      {style.label}
    </span>
  );
}

const SwapQueueTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedPrefillRef = useRef(false);
  const toast = React.useRef<Toast>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [swapQueuePrefill, setSwapQueuePrefill] =
    useState<SwapQueuePrefill | null>(null);
  const [swapQueueRefetchNonce, setSwapQueueRefetchNonce] = useState(0);
  const [bannerSwapQueue, setBannerSwapQueue] = useState<BannerSwap[]>([]);
  const [filteredRows, setFilteredRows] = useState<BannerSwap[]>([]);
  // Deep links (e.g. from the home page) may set ?mine=true&status=failed,partial_failed
  const [mySwapsOnly, setMySwapsOnly] = useState(
    () => parseBooleanParam(searchParams, "mine") === true
  );
  const [selectedRow, setSelectedRow] = useState<BannerSwap | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    swapName: "",
    swapUID: "",
    createdBy: "",
    channelSearch: "",
    status: (() => {
      const fromUrl = parseStringArrayParam(searchParams, "status");
      return fromUrl.length > 0 ? fromUrl : ["scheduled", "running"];
    })() as string[],
  }));
  const [channelTitleById, setChannelTitleById] = useState<
    Map<string, string>
  >(new Map());

  const user = useSelector((state: { googleAuth?: { user?: { email?: string } } }) =>
    state.googleAuth?.user
  );
  const loggedInUserEmail = user?.email;

  useEffect(() => {
    if (consumedPrefillRef.current) {
      return;
    }
    const prefill = parseSwapQueuePrefill(searchParams.toString());
    if (!prefill?.channelIds.length && !prefill?.makeDefault) {
      return;
    }
    consumedPrefillRef.current = true;
    setSwapQueuePrefill(prefill);
    setShowDialog(true);
    // Drop only the prefill params so filter params (mine/status) survive.
    const next = new URLSearchParams(searchParams);
    ["channelId", "channelIds", "makeDefault"].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await bannerDashboardApi.getBannerSwapQueue();
        setBannerSwapQueue(data);
      } catch (error: unknown) {
        if (error instanceof Error && "response" in error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 403) {
            setTimeout(() => void fetchData(), 500);
          }
        }
      }
    };
    void fetchData();
  }, [swapQueueRefetchNonce]);

  useEffect(() => {
    const loadChannelTitles = async () => {
      try {
        const settings = await apiService.channelSettings.getAllChannelSettings();
        const map = new Map<string, string>();
        for (const row of settings) {
          const id = row.ChannelId || (row as { id?: string }).id;
          if (id) {
            map.set(id, row.ChannelTitle || "");
          }
        }
        setChannelTitleById(map);
      } catch {
        setChannelTitleById(new Map());
      }
    };
    void loadChannelTitles();
  }, []);

  const refreshQueue = useCallback(async (focusSwapUID?: string) => {
    const data = await bannerDashboardApi.getBannerSwapQueue();
    setBannerSwapQueue(data);
    if (focusSwapUID) {
      const row = data.find((s) => s.swapUID === focusSwapUID);
      if (row) {
        setSelectedRow(row);
      }
    }
  }, []);

  const { cancelSwap, getRowClassName, isSwapCancelling } =
    useSwapQueueCancellation({
      toast,
      setBannerSwapQueue,
      setFilteredRows,
    });

  const handleCancelSwap = useCallback(
    async (swapUID: string) => {
      trackToolAction("BannerSwap", "cancel_swap");
      setDetailsOpen(false);
      setSelectedRow(null);
      await cancelSwap(swapUID);
    },
    [cancelSwap]
  );

  const handleSearch = useCallback(() => {
    trackToolAction("BannerSwap", "search");
    let filtered = bannerSwapQueue;

    if (mySwapsOnly && loggedInUserEmail) {
      filtered = filtered.filter(
        (r) => r.createdBy?.toLowerCase() === loggedInUserEmail.toLowerCase()
      );
    }

    if (filters.status?.length > 0) {
      filtered = filtered.filter((r) => filters.status.includes(r.status));
    } else {
      filtered = filtered.filter(
        (r) => r.status === "scheduled" || r.status === "running"
      );
    }

    if (filters.swapUID) {
      const q = filters.swapUID.trim().replace(/^#/, "").toLowerCase();
      filtered = filtered.filter((r) =>
        String(r.swapUID).toLowerCase().includes(q)
      );
    }
    if (filters.swapName) {
      const q = filters.swapName.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.swapName?.toLowerCase().includes(q) ||
          String(r.swapUID).toLowerCase().includes(q.replace(/^#/, ""))
      );
    }
    if (filters.createdBy) {
      filtered = filtered.filter((r) => r.createdBy === filters.createdBy);
    }
    if (filters.channelSearch) {
      const q = filters.channelSearch.toLowerCase();
      filtered = filtered.filter((r) =>
        r.channelIDs?.some((id) => {
          const title = channelTitleById.get(id) ?? "";
          return (
            id.toLowerCase().includes(q) || title.toLowerCase().includes(q)
          );
        })
      );
    }

    setFilteredRows(filtered);
  }, [filters, bannerSwapQueue, mySwapsOnly, loggedInUserEmail, channelTitleById]);

  useEffect(() => {
    if (bannerSwapQueue.length > 0) {
      const defaultFiltered = bannerSwapQueue.filter(
        (r) => r.status === "scheduled" || r.status === "running"
      );
      setFilteredRows(defaultFiltered);
    }
  }, [bannerSwapQueue]);

  useEffect(() => {
    handleSearch();
  }, [mySwapsOnly, handleSearch]);

  const handleSetShowDialog: React.Dispatch<React.SetStateAction<boolean>> = (
    value
  ) => {
    setShowDialog((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (!next) {
        setSwapQueuePrefill(null);
      }
      return next;
    });
  };

  const swapDialogHeader =
    swapQueuePrefill?.makeDefault && swapQueuePrefill.channelIds.length === 1
      ? `Set as new default - Channel: ${swapQueuePrefill.channelIds[0]}`
      : swapQueuePrefill?.makeDefault
        ? `Set as new default - ${swapQueuePrefill.channelIds.length} channels`
        : undefined;

  const handleReset = () => {
    setFilters({
      swapName: "",
      swapUID: "",
      createdBy: "",
      channelSearch: "",
      status: ["scheduled", "running"],
    });
    setMySwapsOnly(false);
    setFilteredRows(
      bannerSwapQueue.filter(
        (r) => r.status === "scheduled" || r.status === "running"
      )
    );
  };

  return (
    <BannerDashboardTabLayout>
      <SwapQueueFilterBar
        filters={filters}
        setFilters={setFilters}
        unfilteredData={bannerSwapQueue}
        onSearch={handleSearch}
        onReset={handleReset}
        onQueueNewSwap={() => {
          setSwapQueuePrefill(null);
          setShowDialog(true);
        }}
        mySwapsOnly={mySwapsOnly}
        onMySwapsToggle={setMySwapsOnly}
      />

      <DataTable
        className="mt-4"
        value={filteredRows}
        rowClassName={getRowClassName}
        paginator
        rows={20}
        rowsPerPageOptions={[10, 20, 50, 100]}
        showGridlines
        tableStyle={{ minWidth: "50rem" }}
      >
        <Column
          field="swapUID"
          header="UID"
          sortable
          style={{ width: "6.5rem" }}
          body={(r: BannerSwap) => (
            <button
              type="button"
              className="font-mono text-sm text-left text-gray-800 hover:underline bg-transparent border-0 p-0 cursor-pointer"
              title="Copy swap UID"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard?.writeText(String(r.swapUID));
              }}
            >
              #{r.swapUID}
            </button>
          )}
        />
        <Column field="swapName" header="Task name" sortable />
        <Column
          field="startDate"
          header="Start"
          body={(r: BannerSwap) => (
            <SwapScheduleDateCell
              swap={r}
              queue={bannerSwapQueue}
              field="startDate"
            />
          )}
          sortable
        />
        <Column
          field="endDate"
          header="End"
          body={(r: BannerSwap) => (
            <SwapScheduleDateCell
              swap={r}
              queue={bannerSwapQueue}
              field="endDate"
            />
          )}
          sortable
        />
        <Column field="createdBy" header="User" sortable />
        <Column
          header="Channels"
          body={(r: BannerSwap) => r.channelIDs.length}
          sortable
        />
        <Column
          field="status"
          header="Status"
          body={statusBadge}
          sortable
        />
        <Column
          header="Execution"
          body={(r: BannerSwap) => {
            const summary = channelResultsSummary(r.channelResults);
            return summary ? (
              <span className="text-xs font-medium text-gray-700">{summary}</span>
            ) : (
              <span className="text-xs text-gray-400">—</span>
            );
          }}
        />
        <Column
          header=""
          body={(r: BannerSwap) => (
            <Button
              icon="pi pi-info-circle"
              onClick={() => {
                setSelectedRow(r);
                setDetailsOpen(true);
              }}
            />
          )}
          style={{ width: "4rem" }}
        />
      </DataTable>

      {selectedRow && (
        <SwapQueueDetailsDialog
          rowData={selectedRow}
          bannerSwapQueue={bannerSwapQueue}
          visible={detailsOpen}
          onHide={() => setDetailsOpen(false)}
          onRefreshQueue={refreshQueue}
          onCancelSwap={handleCancelSwap}
          cancelInProgress={isSwapCancelling(selectedRow.swapUID)}
          toast={toast}
        />
      )}

      <BannerSwapForm
        showDialog={showDialog}
        setShowDialog={handleSetShowDialog}
        toast={toast}
        onSwapQueued={() => setSwapQueueRefetchNonce((n) => n + 1)}
        initialChannelIds={swapQueuePrefill?.channelIds}
        initialMakeDefault={swapQueuePrefill?.makeDefault}
        dialogHeader={swapDialogHeader}
      />

      <Toast ref={toast} />
    </BannerDashboardTabLayout>
  );
};

export default SwapQueueTab;
